import { FC, useState } from 'react';
import styled from 'styled-components';

import { useNotices } from '@/features/notices/useNotices';
import { useMembers } from '@/features/vfo/hooks/useMembers';
import { breakpoints, media } from '@/shared/components';
import { COLORS } from '@/shared/constants/colors';
import { getPersonFullName } from '@/shared/helpers/names';
import { Button } from '@/shared/uikitV2/Button';
import { useTranslation } from '@/shared/utils/i18n/useTranslation';
import { useModalsStore } from '@/widgets/modals/modals.store';
import { RemoveMemberModalProps } from '@/widgets/modals/types';

import { ModalContainerV2 } from '../../interwill/containers/ModalContainerV2';

export const RemoveMemberModal: FC<RemoveMemberModalProps> = ({ profileId, onFinish }) => {
  const { t } = useTranslation('widgets/modals', { keyPrefix: 'removeMember' });

  const [isLoading, setIsLoading] = useState(false);
  const notices = useNotices();
  const { members, deleteMember } = useMembers();
  const { closeModal } = useModalsStore();

  const member = members.find((item) => item.profileId === profileId);

  const name = getPersonFullName(member ?? {}, 'display');

  const handleRemove = async () => {
    if (!member) return;

    setIsLoading(true);
    const res = await deleteMember(profileId);
    setIsLoading(false);

    if (res) {
      notices.success(t('successNotice', { fullname: name || t('invitedMember') }));
      closeModal();
      onFinish?.();
    }
  };

  if (!member) return null;

  return (
    <ModalContainerV2 pinBottom title={t('title')}>
      <ListContainer>
        <span>{t('text1')}</span>
        <span>{t.rich('text2', { fullname: name || t('invitedMember') })}</span>
      </ListContainer>
      <Buttons>
        <Button type="default" size="middle" onClick={closeModal}>
          {t('cancel')}
        </Button>
        <ButtonDestructive
          loading={isLoading}
          disabled={isLoading}
          size="middle"
          onClick={handleRemove}
        >
          {t('remove')}
        </ButtonDestructive>
      </Buttons>
    </ModalContainerV2>
  );
};

const ListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  overflow-y: auto;
  padding: 24px 0;
`;

const Buttons = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-top: 16px;
  gap: 16px;

  ${media.md}, ${breakpoints.sm} {
    flex-direction: column-reverse;
  }
`;

const ButtonDestructive = styled(Button)`
  &&& {
    background-color: ${COLORS.systemFlame200};
    color: #fff;
  }
`;
